import axios from 'axios';
import moment from 'moment';
import { makeS3BlobsRenderable } from '../../util/fileHandling';
import { standardizePhoneNumber } from '../../util/standardizePhoneNumber';

const requester = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 50000,
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${process.env.REACT_APP_API_KEY}`,
  },
  withCredentials: true,
});

export const httpClient = {};
Object.keys(requester).forEach(key => {
  httpClient[key] =
    typeof requester[key] === 'function'
      ? async (...args) => {
          return requester[key](...args)
            .then(resp => resp)
            .catch(err => {
              if (err.request && err.request.status === 401) {
                window.location.assign(
                  `${process.env.REACT_APP_PORTAL_URL}/login${
                    err.request.response === 'Timed out' ? '?timedout=true' : ''
                  }`
                );
              } else throw err;
            });
        }
      : requester[key];
});

/////////////////////////////////////////
///// Users /////
/////////////////////////////////////////

export const getUserByIdReq = async userId => {
  const { data } = await httpClient.get(`/api/users/${userId}`);
  return data;
};

export const isEmailAvailable = async (email, administratorUserId) => {
  const { status } = await httpClient.put(`/api/users/email/in-use`, {
    email,
    administratorUserId
  });
  return status === 202 ? true : false;
};

/////////////////////////////////////////
/////            Patients           /////
/////////////////////////////////////////

export const deletePatientsAndAssociationsReq = async patientsIds => {
  const { data } = await httpClient.put(
    '/api/patients/delete-patients-and-assosiations',
    { patientsIds }
  );
  return data;
};

export const fetchPatientsPage = async ({
  searchTerm,
  limit,
  offset,
  practiceId,
  tolerance,
}) => {
  const { data } = await httpClient.put(`/api/patients/matching`, {
    searchTerm,
    limit,
    offset,
    practiceId,
    tolerance,
  });
  return data.map(patient => ({
    id: patient.id,
    name: patient.name,
    firstName: patient.firstName,
    lastName: patient.lastName,
    email: patient.email,
    phone: patient.phone,
    zipcode: patient.zipcode,
    gender: patient.gender,
    birthdate: moment.utc(patient.birthdate).format('MM/DD/YYYY'),
    userId: patient.userId,
    patientTebraId: patient.patientTebraId,
    addressLine1: patient.addressLine1,
    addressLine2: patient.addressLine2,
    city: patient.city,
    state: patient.state,
    insuranceName: patient.insuranceName,
    insuranceId: patient.insuranceId,
    insurancePlanId: patient.insurancePlanId,
    payerId: patient.payerId,
    groupNumber: patient.groupNumber,
  }));
};

export const fetchAllPatientsBasicInfo = async (practiceId, searchTerm) => {
  const { data } = await httpClient.post(
    `/api/patients/all${practiceId ? '/' + practiceId : ''}`,{practiceId,searchTerm}
  );
  return data;
};

export const fetchPatientInsurance = async (patientId, token) => {
  return httpClient.put('/api/patients/insurance/attachments', {
    patientId,
    token,
  });
};

export const deletePatientInsuranceFile = async (patientId, keyName) => {
  return httpClient.put('/api/patients/delete/insuranceFile', {
    patientId,
    keyName,
  });
};

export const fetchSearchPatientInfo = async params => {
  const { data } = await httpClient.put('/api/patients/search', params);
  return data;
};

export const createPatient = async params => {
  const { data } = await httpClient.post('/api/patients', params);
  return data;
};

export const updatePatient = async params => {
  const { data } = await httpClient.put('/api/patients', params);
  return data;
};

export const signupPatient = async ({
  firstName,
  lastName,
  preferredName,
  email,
  phone,
  addressLine1,
  addressLine2,
  city,
  state,
  zipcode,
  birthdate,
  gender,
  password,
  affiliateId,
  affiliateInOffice,
  attachments,
  insuranceName,
  insuranceId,
  insurancePlanId,
  payerId,
  groupNumber,
}) => {
  //  const dob = new Date(birthdate);
  // const cutoff = new Date();

  // cutoff.setFullYear(cutoff.getFullYear() - 18);

  // const isChildPatient =  dob > cutoff;

  const userDetails = {
    firstName,
    lastName,
    preferredName,
    email,
    phone,
    addressLine1,
    addressLine2,
    city,
    state,
    zipcode,
    birthdate,
    gender,
    password,
    affiliateId,
    affiliateInOffice,
    attachments,
    insuranceName,
    insuranceId,
    insurancePlanId,
    payerId,
    groupNumber,
  };

  const response = await httpClient.post('/api/patients/signup', userDetails);
  const patient = response.data;

  return {
    id: patient.id,
    firstName: patient.firstName,
    lastName: patient.lastName,
    email: patient.email,
    gender: patient.gender,
    birthdate: patient.birthdate,
    phone: patient.phone,
    zipcode: patient.zipcode,
    tenancyId: patient.tenancyId,
    insuranceName: patient.insuranceName,
    insuranceId: patient.insuranceId,
  };
};

export const fetchPatientActivity = async patientId => {
  const result = await httpClient.get(`/api/patients/${patientId}/activity`);
  return result.data
    .sort((a, b) => (a.time < b.time ? 1 : -1))
    .map(event => ({
      id: event.id,
      type: event.type,
      receivingProvider: event.receivingProvider,
      time: event.time,
      createdAt: event.createdAt,
      status: event.status,
    }));
};

export const fetchPatientById = async patientId => {
  const result = await httpClient.get(`/api/patients/${patientId}`);
  const patient = result.data || {};
  return patient;
};

export const fetchUserById = async id => {
  const result = await httpClient.get(`/api/users/id/${id}`);
  const currentUser = result.data || {};
  const currentPatient = currentUser.patient || {};
  return {
    id: currentUser.id,
    name: currentUser.name,
    firstName: currentUser.firstName,
    lastName: currentUser.lastName,
    email: currentUser.email,
    phone: currentUser.phone,
    admin: currentUser.admin,
    active: currentUser.active,
    patient: {
      id: currentPatient.id,
      zipcode: currentPatient.zipcode,
      gender: currentPatient.gender,
      birthdate: currentPatient.birthdate,
    },
    isProvider: Boolean(currentUser.provider),
    isAdministrator: Boolean(currentUser.administrator),
    isPatient: Boolean(currentUser.patient),
  };
};

/* Dependents-guardians */
export const creatAssociationReq = async (
  dependentPatientId,
  guardianPatientId
) => {
  return await httpClient.post(`/api/patients/create-dependent-association`, {
    dependentPatientId,
    guardianPatientId,
  });
};

export const getGuardianDependentsReq = async guardianPatientId => {
  const { data } = await httpClient.get(
    `/api/patients/guardian-dependents/${guardianPatientId}`
  );
  return data;
};

export const getGuardianDependentsDetails = async guardianPatientId => {
  const { data } = await httpClient.get(
    `/api/patients/guardian/DependentsInfo/${guardianPatientId}`
  );
  return data;
};

export const getConciergeMemberInfo = async (guardianPatientId, providerId) => {
  const { data } = await httpClient.get(
    `/api/patients/guardian/comparePatient?guardianPatientId=${guardianPatientId}&providerId=${providerId}`
  );
  return data;
};

/////////////////////////////////////////
/////           Providers           /////
/////////////////////////////////////////

export const fetchProvidersPage = async ({
  searchTerm,
  limit,
  offset,
  tolerance,
}) => {
  const { data } = await httpClient.put('/api/providers/matching', {
    searchTerm,
    limit,
    offset,
    tolerance,
  });
  return data;
};

export const fetchAllProvidersBasicInfo = async practiceId => {
  const { data } = await httpClient.get(
    `/api/providers/all${practiceId ? '/' + practiceId : ''}`
  );
  return data;
};

export const updateConsultOptForProvider = async params => {
  const { data } = await httpClient.put(
    `/api/providers/update/add-consult-option`,
    params
  );
  return data;
};

export const fetchAllRestrictedProvidersBasicInfo = async practiceId => {
  const { data } = await httpClient.get(
    `/api/providers/allRestrictedProvider${practiceId ? '/' + practiceId : ''}`
  );
  return data;
};

export const fetchProviderByUrl = async providerUrl => {
  const url = providerUrl.replace(/[/]/g, '+');
  //the '+' can be changed to be any other replacement for '/' while the data is getting fetched
  //see the other end in the qwell app server/routes/controllers/providers

  const { data } = await httpClient.get(`/api/providers/by-profile-url/${url}`);
  return data;
};

export const fetchAllProvidersUrlInfo = async () => {
  const result = await httpClient.get(`/api/providers/all/urls`);
  return result.data;
};

export const fetchProvider = async id => {
  const { data } = await httpClient.get(`/api/providers/${id}`);
  const onlyFields = [
    'name',
    'firstName',
    'lastName',
    'bio',
    'imageUrl',
    'tenancies',
    'degree',
    'fullTitle',
    'providerUrl',
    'id',
    'userId',
    'active',
    'phone',
    'preferredAge',
    'preferredGender',
  ];
  let provider = {};
  for (let field of onlyFields) {
    provider[field] = data[field];
  }
  provider.tenancies = provider.tenancies.filter(tenancy => tenancy.active);
  provider.phone = standardizePhoneNumber(provider.phone);
  return provider;
};

export const fetchProviderPreferredInfo = async id => {
  const { data } = await httpClient.get(`/api/providers/preferredInfo/${id}`);
  return data;
};

export const createProvider = async params => {
  return httpClient.post('/api/providers', params);
};

export const updateProvider = async params => {
  return httpClient.put(`/api/providers`, params);
};

export const updateProviderUrlInfo = async params => {
  return httpClient.put(`/api/providers/providerUrl`, params);
};

export const trackPatient = async params => {
  return httpClient.post(`/api/track-patient`, params);
};

export const updateTrackPatient = async params => {
  return httpClient.put(`/api/track-patient/update`, params);
};

export const fetchCTrackingDetails = async (patientId, trackingId) => {
  // return httpClient.get(
  //   `/api/track-patient?patientId=${patientId}&trackingId=${trackingId}`
  // );
  let params = {
    patientId,
    trackingId,
  };
  return httpClient.put(`/api/track-patient/get`, params);
};

export const fetchTrackAttachments = consultNoteId => {
  return httpClient.get(`/api/track-patient/attachments/${consultNoteId}`);
};

export const fetchTrackingReports = async () => {
  return httpClient.get(`/api/track-patient/track-chart/count`);
};

export const fetchTrackingList = async (
  trackId,
  patientId,
  offset,
  limit,
  trackStatus,
  startDate,
  endDate,
  typeOfTrack
) => {
  const { data } = await httpClient.put(`/api/track-patient`, {
    trackId,
    patientId,
    offset,
    limit,
    trackStatus,
    startDate,
    endDate,
    typeOfTrack,
  });
  return data;
};

export const unTagPatientById = async (trackId, trackStatus) => {
  return httpClient.put(`/api/track-patient/un-track-patient`, {
    trackId,
    trackStatus,
  });
};

// /api/track-patient/patient/id
export const fetchPatientTrackerInfo = async id => {
  const { data } = await httpClient.get(`/api/track-patient/patient/${id}`);
  return data;
};

// /api/track-patient/:trackId
export const deleteTrackPatient = async trackId => {
  return httpClient.delete(`/api/track-patient/${trackId}`);
};

export const fetchProviders = async ({
  userId,
  practiceId,
  providerId,
  specialtyId,
  insuranceId,
  providerName,
  cityShortName,
  specialtyShortName,
  insuranceShortName,
  limit,
  offset,
} = {}) => {
  // using post as opposed to get so we can use a body instead of putting params in url
  const { data } = await httpClient.post(`/api/providers/search`, {
    userId,
    practiceId,
    providerId,
    specialtyId,
    insuranceId,
    providerName,
    cityShortName,
    specialtyShortName,
    insuranceShortName,
    limit,
    offset,
  });
  return data;
};

export const fetchAllProvidersForPractice = async ({
  practiceId,
  providerId,
  specialtyId,
  insuranceId,
  providerName,
  cityShortName,
  specialtyShortName,
  insuranceShortName,
} = {}) => {
  // using post as opposed to get so we can use a body instead of putting params in url
  const { data } = await httpClient.post(`/api/providers/fetch`, {
    practiceId,
    providerId,
    specialtyId,
    insuranceId,
    providerName,
    cityShortName,
    specialtyShortName,
    insuranceShortName,
  });
  return data;
};

export const activateProvider = async ({ id: providerId }) => {
  return httpClient.post('/api/providers/activate', { providerId });
};

export const deactivateProvider = async ({ id: providerId }) => {
  return httpClient.delete(`/api/providers/activate/${providerId}`);
};

////////////////////////////////////////////////////
/////          PROVIDER"S AVAILABILITY         /////
////////////////////////////////////////////////////

export const enableProvider = async obj => {
  console.log('ENABLE PROVIDER API CALLED');
  return httpClient.post('/api/providers/enable', obj);
};

export const disableProvider = async obj => {
  console.log('disable');
  return httpClient.delete(`/api/providers/disable/${obj.id}`);
};

/////////////////////////////////////////
/////          Appointments         /////
/////////////////////////////////////////

export const createAppointmentRequest = async params => {
  const result = await httpClient.post(
    '/api/specialties/appointment-requests/',
    params
  );
  return result.data;
};

export const alterAppt = async (where, newInfo) => {
  const { data } = await httpClient.put(`/api/appointments/update`, {
    where,
    newInfo,
  });
  return data;
};

export const fetchProviderAppointments = async (
  providerId,
  startDay,
  endDay,
  tenancyType,
  zipcode
) => {
  const from = moment(startDay).format('MM-DD-YYYY');
  const to = moment(endDay)
    .add(1, 'days')
    .format('MM-DD-YYYY');
  const { data } = await httpClient.get(
    `/api/appointments/provider/${providerId}/from/${from}/to/${to}/${tenancyType}/${zipcode}`
  );
  return data;
};

export const fetchPracticeAppointments = async (practiceId, date) => {
  date = moment(date).format('MM-DD-YY');
  const { data } = await httpClient.get(
    `/api/appointments/practice/${practiceId}/from/${date}/to/${date}`
  );
  return data;
};

export const getProviderApptCountWithDates = async (
  providerId,
  startDay,
  endDay
) => {
  const from = moment(startDay).format('MM-DD-YYYY');
  const to = moment(endDay).format('MM-DD-YYYY');
  const { data } = await httpClient.get(
    `/api/appointments/providerApptCount/${providerId}/from/${from}/to/${to}`
  );
  return data;
};

export const fetchPatientAppointments = async (patientId, limit, offset) => {
  const { data } = await httpClient.get(
    `/api/appointments/patient?patientId=${patientId}&limit=${limit}&offset=${offset}`
  );
  return data;
};

export const fetchPatientIndividualAppts = async (
  patientId,
  status,
  limit,
  offset
) => {
  const { data } = await httpClient.get(
    `/api/appointments/patientAppts?patientId=${patientId}&status=${status}&limit=${limit}&offset=${offset}`
  );
  return data;
};

export const fetchPatientApptAnalysisReport = async patientId => {
  const { data } = await httpClient.get(
    `/api/appointments/patientAnalysis/report?patientId=${patientId}`
  );
  return data;
};

export const fetchCancelledPatientAppointments = async (
  patientId,
  limit,
  offset
) => {
  const { data } = await httpClient.get(
    `/api/appointments/canceled/patient?patientId=${patientId}&limit=${limit}&offset=${offset}`
  );
  return data;
};

export const updateProviderUrl = async (id, providerUrl) => {
  return httpClient.put(`/api/providers/updateUrl`, { id, providerUrl });
};

export const mergeDuplicatesPatients = async (
  primPatientId,
  dupPatientsIds
) => {
  return httpClient.put(`/api/patients/merge-duplicates`, {
    primPatientId,
    dupPatientsIds,
  });
};

/////////////////////////////////////////
/////           Tenancies           /////
/////////////////////////////////////////

export const createVirtualRoomUrl = (providerId, virtualRoomUrl) => {
  return httpClient.post(`/api/tenancies/createVirtualRoomUrl`, {
    providerId,
    virtualRoomUrl,
  });
};

/////////////////////////////////////////
/////         Consult Notes         /////
/////////////////////////////////////////

export const getPatientConsultNotes = async (patientId, limit, offset) => {
  const notes = await httpClient.get(
    `/api/consult-notes/patient/patientId?patientId=${patientId}&limit=${limit}&offset=${offset}`
  );
  return notes.data;
};

export const fetchPatientReferrals = async (patientId, limit, offset) => {
  const notes = await httpClient.get(
    `/api/consult-notes/referrals/patient?patientId=${patientId}&limit=${limit}&offset=${offset}`
  );
  return notes.data;
};

export const fetchConsultNotesPage = async (
  archived,
  limit,
  providersSearchId,
  patientsSearchId,
  offset,
  userId,
  providerID,
  isAdmin
) => {
  const { data } = await httpClient.put(`/api/consult-notes/matching`, {
    archived,
    limit,
    providersSearchId,
    patientsSearchId,
    offset,
    userId,
    providerID,
    isAdmin
  });
  return (data || []).map(note => ({
    id: note.id,
    body: note.body,
    patientId: note.patient.id,
    patientName: note.patient.name,
    toProviderId: note.toProvider.id,
    toProviderName: note.toProvider.name,
    toProviderImageUrl: note.toProvider.imageUrl,
    fromProviderId: note.fromProvider.id,
    fromProviderName: note.fromProvider.name,
    fromProviderImageUrl: note.fromProvider.imageUrl,
    archivedById: note.archivedById,
    createdAt: note.createdAt,
    visibility: note.visibility,
  }));
};

// export const fetchTrackingList = async (
//   id
// ) => {
//   const { data } = await httpClient.get(`api/track-patient/${id}`);
//   return data;
// };

/////////////////////////////////////////
/////  Attachments - Consult Notes  /////
/////////////////////////////////////////

export const fetchConsultNoteAttachments = consultNoteId => {
  return httpClient.get(`/api/consult-notes/attachments/${consultNoteId}`);
};

export const fetchAppointmentAttachments = AppointmentId => {
  return httpClient.get(`/api/appointments/attachments/${AppointmentId}`);
};

// export const getConsultNotes = async patientId => {
//   const notes = await httpClient.get(
//     `/api/consult-notes/patients/${patientId}/`
//   );
//   return notes.data;
// };

export const getConsultNotes = async (patientId, limit, offset) => {
  const notes = await httpClient.put(
    `/api/consult-notes/patients/${patientId}/`,
    {
      limit,
      offset,
    }
  );
  return notes.data;
};

export const getChildConsultNotes = async (patientId, limit, offset) => {
  const notes = await httpClient.put(
    `/api/consult-notes/child-notes/${patientId}/`,
    {
      limit,
      offset,
    }
  );
  return notes.data;
};

// export const getChildConsultNotes = async patientId => {
//   const notes = await httpClient.put(
//     `/api/consult-notes/child-notes/${patientId}/`
//   );
//   return notes.data;
// };

export const fetchAdminConsultNotes = async (archived, offset) => {
  const { data } = await httpClient.get(
    `/api/consult-notes/admin?archived=${archived}&offset=${offset}&limit=20`
  );

  return (data || []).map(note => ({
    id: note.id,
    body: note.body,
    patientId: note.patient.id,
    patientName: note.patient.name,
    sendToQwellProviderName: note.sendToQwellProviderName,
    sendToQwellProviderNpi: note.sendToQwellProviderNpi,
    sendToQwell: note.sendToQwell,
    fromProviderId: note.fromProvider.id,
    fromProviderName: note.fromProvider.name,
    fromProviderImageUrl: note.fromProvider.imageUrl,
    createdAt: note.createdAt,
    archivedById: note.archivedById,
    sentAt: note.sentAt,
  }));
};

export const createConsultNote = async noteParams => {
  return httpClient.post('/api/consult-notes', noteParams);
};

export const archiveConsultNote = async (currentUser, note) => {
  return httpClient.get(
    `/api/consult-notes/archive/${note.id}/${currentUser.id}`
  );
};

export const unarchiveConsultNote = async (note,currentUser) => {
  return httpClient.get(`/api/consult-notes/unarchive/${note.id}/${currentUser.id}`);
};

export const sendConsultNote = async noteParams => {
  return httpClient.post('/api/consult-notes/send', noteParams);
};

export const fetchAuthenticatedConsultNote = async authenticatedToken => {
  const { data } = await httpClient.get(
    `/api/consult-notes/auth/${authenticatedToken}`
  );
  const note = data || {};
  return {
    id: note.id,
    body: note.body,
    patientName: note.patient.name,
    patientDOB: note.patient.dob,
    fromProviderName: note.fromProvider.name,
    sentAt: note.sentAt,
  };
};

// Check for any existing appointment.
export const overBookedAppt = async appointmentParams => {
  const result = await httpClient.post(
    '/api/appointments/over-booked-appt',
    appointmentParams
  );

  return result.data;
};

export const bookAppointment = async appointmentParams => {
  const result = await httpClient.post(
    '/api/appointments/book',
    appointmentParams
  );
  // const result = await httpClient.put(
  //   '/api/appointments/bookAppointment/',
  //   appointmentParams
  // );

  return result.data;
};

/////////////////////////////////////////
/////     Files Upload/Download     /////
/////////////////////////////////////////

export const setFileReq = async (prefix, file) => {
  return httpClient.post('/api/files-actions/set-file', { prefix, file });
};

export const setFilesZipReq = async (prefixes, files) => {
  return httpClient.post('/api/files-actions/set-files-zip', {
    prefixes,
    files,
  });
};

/* Note! the below request's type is put instead of get because the 'prefix' contains '/' signs */
export const getFileReq = async prefix => {
  const s3BlobsObj = await httpClient.put(`/api/files-actions/get-file`, {
    prefix,
  });
  const filesObjects = makeS3BlobsRenderable(s3BlobsObj.data);
  return filesObjects;
};

/* Note! the below request's type is put instead of get because the passed 'prefix' contains '/' signs */
export const getFilesMultiPrefixesReq = async prefixes => {
  const s3BlobsObj = await httpClient.put(
    `/api/files-actions/get-files-multi-prefixes`,
    { prefixes }
  );
  const filesObjects = s3BlobsObj.data.map(prefixRes =>
    makeS3BlobsRenderable(prefixRes)
  );
  return filesObjects;
  /*
  filesObjects =[
    [
      { dataUrl, fileType },
      ....
    ]
    .....
  ]
   */
};

/////////////////////////////////////////
/////         Notifications         /////
/////////////////////////////////////////

export const fetchAllNotifications = async params => {
  const notifications = await httpClient.get(
    `/api/notifications${params ? params : ''}`
  );
  return notifications.data;
};

export const fetchAllNotificationsSearch = async params => {
  const notifications = await httpClient.get(
    `/api/notifications/getAll${params ? params : ''}`
  );
  return notifications.data;
};

export const updateToggle = async noteParams => {
  await httpClient.put(`/api/consult-notes`, noteParams);
  return true;
};

// the following functions arent dry and im questioning if they should be
// this way but i think its okay for now cause passing another param
// is the alternative and it feels so ugly
export const markNotificationArchived = async notificationId => {
  const notification = await httpClient.put(`/api/notifications/archive/`, {
    notificationId,
  });
  return notification.data;
};

export const markNotificationUnarchived = async notificationId => {
  const notification = await httpClient.put(`/api/notifications/unarchive/`, {
    notificationId,
  });
  return notification.data;
};

export const markNotificationAsRead = async notificationId => {
  const notification = await httpClient.put(`/api/notifications/read/`, {
    notificationId,
  });
  return notification.data;
};

export const requestUpdateNotificationPortalNote = async idAndNoteBody => {
  const notification = await httpClient.put(
    `/api/notifications/update-portal-note/`,
    idAndNoteBody
  );
  return notification.data;
};

/////////////////////////////////////////
/////       Patient Notification    /////
/////////////////////////////////////////

export const getPatientAllMessages = async patientId => {
  const patientMessage = await httpClient.get(
    `/api/patientNotification/getMessages?patientId=${patientId}`
  );
  return patientMessage.data;
};

export const markReadStatusOnPatientNotification = async (params, type) => {
  const markReadMessage = await httpClient.put(
    `/api/patientNotification/read`,
    { params, type }
  );
  return markReadMessage.data;
};

/////////////////////////////////////////
/////       Activity Reports        /////
/////////////////////////////////////////

export const fetchIncomingWebAppts = async (providerId, end, start = null) => {
  let result;
  if (start) {
    result = await httpClient.get(
      `/api/activity-reports/from-site/provider/${providerId}/${end}/${start}`
    );
  } else {
    result = await httpClient.get(
      `/api/activity-reports/from-site/provider/${providerId}/${end}`
    );
  }
  return result.data;
};

export const fetchIncomingProviderAppts = async (
  providerId,
  end,
  start = null
) => {
  let result;
  if (start) {
    result = await httpClient.get(
      `/api/activity-reports/referrals/provider/${providerId}/${end}/${start}`
    );
  } else {
    result = await httpClient.get(
      `/api/activity-reports/referrals/provider/${providerId}/${end}`
    );
  }
  return result.data;
};

export const fetchOutgoingProviderAppts = async (
  providerId,
  end,
  start = null
) => {
  let result;
  if (start) {
    result = await httpClient.get(
      `/api/activity-reports/referred/provider/${providerId}/${end}/${start}`
    );
  } else {
    result = await httpClient.get(
      `/api/activity-reports/referred/provider/${providerId}/${end}`
    );
  }
  return result.data;
};

export const fetchFullActivityReport = async (startDate, endDate) => {
  const result = await httpClient.get(
    `/api/activity-reports/full/from/${startDate}/to/${endDate}`
  );
  return result.data;
};

export const checkSubscriptions = async () => {
  const { status } = await httpClient.get(`/api/payment/subscription/status`);
  return status === 200;
};

export const cancelSubscriptions = async () => {
  await httpClient.delete(`/api/payment/subscription/cancel`);
};

export const fetchPatientAttachements = async (
  patientId,
  key,
  limit,
  offset
) => {
  await httpClient.put('/api/patientAttachements/allFiles', {
    patientId,
    key,
    limit,
    offset,
  });
};

export const fetchSubscriptedInfo = async () => {
  const result = await httpClient.get(
    '/api/payment/subscription/getCustomerInfo'
  );
  return result.data;
};

export const createProviderViewDateBookList = async dependentProviderId => {
  await httpClient.post('/api/providerDateBook/checkProvider', {
    dependentProviderId,
  });
};

export const fetchAllZipCodes = async (date) => {
  const result = await httpClient.put(`/api/providerDateBook/getProvidersScheduledZipcodes`, { startDate: date });
  return result.data
};

export const fetchFullProviderDateBook = async (startDay, tenancyType, zipcode) => {
  const result = await httpClient.put('/api/providerDateBook/withDate', {
    startDate: startDay,
  });
  const selectedDate = startDay;
  return await Promise.all(
    result &&
      result.data.map(async result => {
        const providerId = result.id;
        const getProviderApptCount = await filterProviderApptCount(
          providerId,
          selectedDate,
          tenancyType,
          zipcode
        );

        let finalValue = ''
        for(let i=0; i < getProviderApptCount.zipcodeArray.length; i++){
            finalValue += `${getProviderApptCount.zipcodeArray[i].zipcodeName} - ${getProviderApptCount.zipcodeArray[i].count}${i+1 != getProviderApptCount.zipcodeArray.length ? ',' : ''} `
        }

        return {
          dateBookId: result.id,
          id: result && result.id,
          apptCount: getProviderApptCount.count || 0,
          zipcodeData: finalValue,
          name: result && result.name,
          fullTitle: result && result.fullTitle,
          firstName: result && result.firstName,
          lastName: result && result.lastName,
          imageUrl: result && result.imageUrl,
          tenancies: result && result.tenancies,
        };
      })
  );
};

const filterProviderApptCount = async (providerId, startDate, tenancyType, zipcode) => {
  let count = 0;
  const getProviderAppts = await fetchProviderApptCount(providerId, startDate, tenancyType, zipcode);
  const dayStart = moment(startDate)
    .startOf('day')
    .format('YYYY-MM-DD');

  let zipcodeArray = [];
  for (let i = 0; i < getProviderAppts.length; i++) {
    const compareDate = moment(getProviderAppts[i].appointmentTime).format(
      'YYYY-MM-DD'
    );
    if (compareDate === dayStart) {
      const existingItem = zipcodeArray.find(zip => zip.zipcodeName === getProviderAppts[i].zipcode);
      if (existingItem) {
        existingItem.count++;
      } else {
        zipcodeArray.push({ zipcodeName: getProviderAppts[i].zipcode, count: 1 });
      }
      count++;
    }
  }
  return {zipcodeArray, count};
};

export const fetchProviderApptCount = async (providerId, selectedDate, tenancyType, zipcode) => {
  const result = await httpClient.put(
    '/api/providerDateBook/getApptProviderCount',
    {
      providerId,
      selectedDate,
      tenancyType,
      zipcode
    }
  );
  return result.data;
};

export const removeDependentInDateBook = async dependentProviderId => {
  await httpClient.put('/api/providerDateBook/matching/uncheckProvider', {
    dependentProviderId,
  });
};

export const getSearchData = async value => {
  const { data } = await httpClient.post('/api/getSuggestion', value);
  return data;
};

export const getFilterData = async value => {
  const { data } = await httpClient.post('/api/specialties/searches', value);
  // const { data } = await httpClient.post('/api/specialties/oldsearches', value);
  return data;
};

export const fetchWebAccountPercentage = async(start,end)=>{
  let result;
  result = await httpClient.get(`/api/appointments/getAppointmentCount/?fromDate=${start}&toDate=${end}`,{responseType:'blob'});
  return result;
};

export const fetchUtilizationReport = async(startDateUTC, endDateUTC, reportPreference, selectedLocationName, selectedLocationValue, selectedProviderId)=>{
  try{
  let result;
  result = await httpClient.post('/api/appointments/getProviderTenancyUtilizationReport',{startDateUTC, endDateUTC, reportPreference, selectedLocationName, selectedLocationValue, selectedProviderId},{ responseType: "blob" });
  if(result.status === 200)
  {
  const blob = new Blob([result.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = (reportPreference==='provider') ? "provider-productivity-report.xlsx" : "location-productivity-report.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

if(result.status === 204){
  return {
    data: null,
    message: "No data available for the selected date range and provider."
  }
}
  }
catch(error){
  console.error("Error fetching utilization report:", error);
  return {
    data: null,
    message: "An error occurred while fetching the report. Please try again later."
  }
}
};